import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router";
import { toast } from "sonner";
import { storage } from "../utils/storage";
import type {
  Vehicle,
  MaintenanceSchedule,
  MaintenanceScheduleGeneration,
  UserMaintenanceSchedule,
  ServiceCategory,
} from "../types";
import { Link } from "react-router";
import {
  AlertTriangle,
  Clock,
  ChevronDown,
  ChevronRight,
  Info,
  X,
  ClipboardCheck,
} from "lucide-react";
import { differenceInDays } from "date-fns";
import { ScheduleGenerationStatus } from "./ScheduleGenerationStatus";

interface MergedScheduleItem {
  schedule: MaintenanceSchedule;
  userSchedule?: UserMaintenanceSchedule;
  daysUntilDue?: number;
  milesUntilDue?: number;
  isOverdue: boolean;
  urgency: "overdue" | "due-soon" | "future";
}

const CATEGORY_ORDER: ServiceCategory[] = [
  "Engine Oil",
  "Transmission",
  "Brakes",
  "Tires",
  "Filters",
  "Fluids",
  "Belts & Hoses",
  "Battery",
  "Inspection",
  "Other",
];

function getPriorityColor(priority?: string) {
  switch (priority) {
    case "critical":
      return "bg-red-100 text-red-700";
    case "recommended":
      return "bg-blue-100 text-blue-700";
    case "optional":
      return "bg-gray-100 text-gray-600";
    default:
      return "bg-gray-100 text-gray-600";
  }
}

function getUrgencyStyles(urgency: string) {
  switch (urgency) {
    case "overdue":
      return "border-l-4 border-l-red-500 bg-red-50";
    case "due-soon":
      return "border-l-4 border-l-yellow-500 bg-yellow-50";
    default:
      return "border-l-4 border-l-gray-200";
  }
}

export function MaintenanceSchedulePage() {
  const [searchParams] = useSearchParams();
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [selectedVehicleId, setSelectedVehicleId] = useState<string>("");
  const [schedules, setSchedules] = useState<MaintenanceSchedule[]>([]);
  const [userSchedules, setUserSchedules] = useState<UserMaintenanceSchedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [generation, setGeneration] = useState<MaintenanceScheduleGeneration | null>(null);
  const [schedulesLoading, setSchedulesLoading] = useState(false);
  const [severeDriving, setSevereDriving] = useState(false);
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(new Set());

  useEffect(() => {
    async function loadVehicles() {
      try {
        const data = await storage.getVehicles();
        setVehicles(data);

        const paramVehicleId = searchParams.get("vehicle");
        if (paramVehicleId && data.some((v) => v.id === paramVehicleId)) {
          setSelectedVehicleId(paramVehicleId);
        } else if (data.length > 0) {
          setSelectedVehicleId(data[0].id);
        }
      } catch (error) {
        console.error("Error loading vehicles:", error);
      } finally {
        setLoading(false);
      }
    }
    loadVehicles();
  }, [searchParams]);

  useEffect(() => {
    if (!selectedVehicleId) return;

    async function loadSchedules() {
      setSchedulesLoading(true);
      try {
        const vehicle = vehicles.find((v) => v.id === selectedVehicleId);
        const [scheduleData, userScheduleData, generationData] = await Promise.all([
          storage.getSchedulesForVehicle(selectedVehicleId),
          storage.getUserSchedulesForVehicle(selectedVehicleId),
          vehicle
            ? storage.getGenerationStatus(vehicle.make, vehicle.model, vehicle.year)
            : Promise.resolve(null),
        ]);

        setSchedules(scheduleData);
        setGeneration(generationData);

        if (userScheduleData.length === 0 && scheduleData.length > 0) {
          await storage.initializeUserSchedules(selectedVehicleId, scheduleData);
          const initialized = await storage.getUserSchedulesForVehicle(selectedVehicleId);
          setUserSchedules(initialized);
        } else {
          setUserSchedules(userScheduleData);
        }
      } catch (error) {
        console.error("Error loading schedules:", error);
      } finally {
        setSchedulesLoading(false);
      }
    }
    loadSchedules();
  }, [selectedVehicleId, vehicles]);

  const selectedVehicle = vehicles.find((v) => v.id === selectedVehicleId);

  const filteredSchedules = useMemo(() => {
    return schedules.filter((s) => s.isSevereDriving === severeDriving);
  }, [schedules, severeDriving]);

  const mergedItems = useMemo((): MergedScheduleItem[] => {
    const now = new Date();
    const currentMileage = selectedVehicle?.currentMileage ?? 0;

    return filteredSchedules
      .map((schedule) => {
        const userSchedule = userSchedules.find(
          (us) => us.scheduleId === schedule.id && !us.isDismissed
        );

        let daysUntilDue: number | undefined;
        let milesUntilDue: number | undefined;
        let isOverdue = false;

        if (userSchedule?.nextDueDate) {
          daysUntilDue = differenceInDays(new Date(userSchedule.nextDueDate), now);
          if (daysUntilDue < 0) isOverdue = true;
        }

        if (userSchedule?.nextDueMileage) {
          milesUntilDue = userSchedule.nextDueMileage - currentMileage;
          if (milesUntilDue < 0) isOverdue = true;
        }

        let urgency: "overdue" | "due-soon" | "future" = "future";
        if (isOverdue) {
          urgency = "overdue";
        } else if (
          (daysUntilDue != null && daysUntilDue <= 30) ||
          (milesUntilDue != null && milesUntilDue <= 1000)
        ) {
          urgency = "due-soon";
        }

        return { schedule, userSchedule, daysUntilDue, milesUntilDue, isOverdue, urgency };
      })
      .filter((item) => item.userSchedule); // Only show items that have user schedules
  }, [filteredSchedules, userSchedules, selectedVehicle]);

  const groupedByCategory = useMemo(() => {
    const groups = new Map<ServiceCategory, MergedScheduleItem[]>();
    for (const item of mergedItems) {
      const cat = item.schedule.serviceCategory;
      if (!groups.has(cat)) groups.set(cat, []);
      groups.get(cat)!.push(item);
    }
    // Sort by defined category order
    return CATEGORY_ORDER.filter((cat) => groups.has(cat)).map((cat) => ({
      category: cat,
      items: groups.get(cat)!,
    }));
  }, [mergedItems]);

  const toggleCategory = (category: string) => {
    setCollapsedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  const handleDismiss = async (userScheduleId: string) => {
    try {
      await storage.dismissScheduleItem(userScheduleId);
      setUserSchedules((prev) =>
        prev.map((us) => (us.id === userScheduleId ? { ...us, isDismissed: true } : us))
      );
    } catch (error) {
      console.error("Error dismissing schedule item:", error);
      toast.error("Failed to dismiss maintenance item. Please try again.");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  if (vehicles.length === 0) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900">Maintenance Schedules</h2>
          <p className="text-gray-600 mt-1">
            AI-generated maintenance schedules for your vehicles
          </p>
        </div>
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <p className="text-gray-600">Add a vehicle first to see its maintenance schedule.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-gray-900">Maintenance Schedules</h2>
        <p className="text-gray-600 mt-1">
          AI-generated maintenance schedules for your vehicles
        </p>
      </div>

      {/* Vehicle selector and driving toggle */}
      <div className="bg-white rounded-lg shadow p-4 flex flex-col sm:flex-row gap-4 items-start sm:items-center">
        <div className="flex-1">
          <label htmlFor="vehicle-select" className="block text-sm font-medium text-gray-700 mb-1">
            Vehicle
          </label>
          <select
            id="vehicle-select"
            value={selectedVehicleId}
            onChange={(e) => setSelectedVehicleId(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            {vehicles.map((v) => (
              <option key={v.id} value={v.id}>
                {v.year} {v.make} {v.model}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Driving Conditions</label>
          <div className="flex rounded-lg border border-gray-300 overflow-hidden">
            <button
              onClick={() => setSevereDriving(false)}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                !severeDriving
                  ? "bg-blue-600 text-white"
                  : "bg-white text-gray-700 hover:bg-gray-50"
              }`}
            >
              Normal
            </button>
            <button
              onClick={() => setSevereDriving(true)}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                severeDriving
                  ? "bg-blue-600 text-white"
                  : "bg-white text-gray-700 hover:bg-gray-50"
              }`}
            >
              Severe
            </button>
          </div>
        </div>
      </div>

      {/* Generation status for selected vehicle */}
      {selectedVehicle && schedules.length === 0 && !schedulesLoading && (
        <ScheduleGenerationStatus
          make={selectedVehicle.make}
          model={selectedVehicle.model}
          year={selectedVehicle.year}
          vehicleId={selectedVehicle.id}
        />
      )}

      {/* Schedule groups */}
      {schedulesLoading ? (
        <div className="flex items-center justify-center h-32">
          <div className="text-gray-600">Loading schedules...</div>
        </div>
      ) : (
        <div className="space-y-4">
          {groupedByCategory.map(({ category, items }) => {
            const isCollapsed = collapsedCategories.has(category);
            const overdueCount = items.filter((i) => i.urgency === "overdue").length;
            const dueSoonCount = items.filter((i) => i.urgency === "due-soon").length;

            return (
              <div key={category} className="bg-white rounded-lg shadow overflow-hidden">
                <button
                  onClick={() => toggleCategory(category)}
                  className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    {isCollapsed ? (
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                    <h3 className="text-lg font-semibold text-gray-900">{category}</h3>
                    <span className="text-sm text-gray-500">({items.length})</span>
                  </div>
                  <div className="flex gap-2">
                    {overdueCount > 0 && (
                      <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">
                        {overdueCount} overdue
                      </span>
                    )}
                    {dueSoonCount > 0 && (
                      <span className="px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-700">
                        {dueSoonCount} due soon
                      </span>
                    )}
                  </div>
                </button>

                {!isCollapsed && (
                  <div className="border-t divide-y">
                    {items.map((item) => (
                      <ScheduleItemRow
                        key={item.schedule.id}
                        item={item}
                        vehicleId={selectedVehicleId}
                        onDismiss={handleDismiss}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {groupedByCategory.length === 0 && !schedulesLoading && schedules.length > 0 && (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <p className="text-gray-600">
                No schedules found for {severeDriving ? "severe" : "normal"} driving conditions.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Confidence disclaimer */}
      {schedules.length > 0 && (
        <div className="bg-gray-50 rounded-lg p-4 flex items-start gap-3">
          <Info className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-gray-500 space-y-1">
            {generation && (
              <p>
                {generation.confidenceScore != null && (
                  <span>
                    Schedule confidence: {Math.round(generation.confidenceScore * 100)}%.{" "}
                  </span>
                )}
                {generation.sourcesUsed && generation.sourcesUsed.length > 0 && (
                  <span>Sources: {generation.sourcesUsed.join(", ")}.</span>
                )}
              </p>
            )}
            <p>
              Maintenance schedules are AI-generated based on manufacturer recommendations and may
              not cover all scenarios. Always consult your owner's manual or a qualified mechanic
              for definitive maintenance guidance.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function ScheduleItemRow({
  item,
  vehicleId,
  onDismiss,
}: {
  item: MergedScheduleItem;
  vehicleId: string;
  onDismiss: (id: string) => void;
}) {
  const { schedule, userSchedule, daysUntilDue, milesUntilDue, urgency } = item;

  return (
    <div className={`px-6 py-4 ${getUrgencyStyles(urgency)}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="font-medium text-gray-900">{schedule.serviceName}</h4>
            {schedule.priority && (
              <span
                className={`px-2 py-0.5 rounded text-xs font-medium ${getPriorityColor(schedule.priority)}`}
              >
                {schedule.priority}
              </span>
            )}
            {urgency === "overdue" && (
              <AlertTriangle className="w-4 h-4 text-red-500" role="img" aria-label="Overdue" />
            )}
            {urgency === "due-soon" && (
              <Clock className="w-4 h-4 text-yellow-500" role="img" aria-label="Due soon" />
            )}
          </div>

          {schedule.description && (
            <p className="text-sm text-gray-600 mt-1">{schedule.description}</p>
          )}

          <div className="flex flex-wrap gap-x-6 gap-y-1 mt-2 text-sm text-gray-500">
            {schedule.intervalMonths && (
              <span>Every {schedule.intervalMonths} months</span>
            )}
            {schedule.intervalMiles && (
              <span>Every {schedule.intervalMiles.toLocaleString()} miles</span>
            )}
            {userSchedule?.nextDueDate && (
              <span>
                Due: {new Date(userSchedule.nextDueDate).toLocaleDateString()}
                {daysUntilDue != null && (
                  <span className="ml-1">
                    ({daysUntilDue < 0 ? `${Math.abs(daysUntilDue)}d overdue` : `in ${daysUntilDue}d`})
                  </span>
                )}
              </span>
            )}
            {userSchedule?.nextDueMileage && (
              <span>
                Due at: {userSchedule.nextDueMileage.toLocaleString()} mi
                {milesUntilDue != null && (
                  <span className="ml-1">
                    ({milesUntilDue < 0 ? `${Math.abs(milesUntilDue).toLocaleString()} mi overdue` : `${milesUntilDue.toLocaleString()} mi left`})
                  </span>
                )}
              </span>
            )}
            {userSchedule?.lastCompletedAt && (
              <span>Last: {new Date(userSchedule.lastCompletedAt).toLocaleDateString()}</span>
            )}
          </div>

          {(schedule.estimatedCostMin != null || schedule.estimatedCostMax != null) && (
            <p className="text-sm text-gray-500 mt-1">
              Est. cost: ${schedule.estimatedCostMin ?? "?"} - ${schedule.estimatedCostMax ?? "?"}
            </p>
          )}
        </div>

        <div className="flex items-center gap-1 flex-shrink-0">
          <Link
            to={`/maintenance?vehicleId=${vehicleId}&type=${encodeURIComponent(schedule.serviceName)}`}
            className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
            title="Log Service"
          >
            <ClipboardCheck className="w-4 h-4" />
          </Link>
          {userSchedule && (
            <button
              onClick={() => onDismiss(userSchedule.id)}
              className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
              title="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
